// variant-inventory.js - FINAL: extends cart pre-order to also trigger when quantity exceeds stock
class VariantInventory {
  constructor() {
    this.productCache = new Map();
    this.cartItemVariantCache = new Map(); // cache for cart item variants
    this.currentVariantId = null;          // track last updated variant (product page)
    this._inventoryUpdateTimer = null;     // debounce timer for product-page badge updates
    this.setupEventDelegation();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      setTimeout(() => this.init(), 1000);
    }
  }
  
  init() {
    console.log('🎯 Inventory system initializing...');
    this.setupMutationObserver();
    this.updateCartInventoryBadges();
    this.initQuickviewPreorder();
    console.log('✅ Inventory system ready');
  }
  
  initQuickviewPreorder() {
    const observer = new MutationObserver(() => {
      const container = document.querySelector('.quickview-popup__container, .quickview-modal, [data-quickview-container]');
      if (container && !container.hasAttribute('data-preorder-initialized')) {
        container.setAttribute('data-preorder-initialized', 'true');
        this.setupQuickviewPreorder(container);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    
    const existing = document.querySelector('.quickview-popup__container, .quickview-modal, [data-quickview-container]');
    if (existing && !existing.hasAttribute('data-preorder-initialized')) {
      existing.setAttribute('data-preorder-initialized', 'true');
      this.setupQuickviewPreorder(existing);
    }
  }
  
  async setupQuickviewPreorder(container) {
    console.log('🔧 Setting up quickview pre-order');
    
    // === READ INJECTED INVENTORY DATA ===
    let variants = null;
    const injectedScript = container.querySelector('#qv-variant-data');
    if (injectedScript) {
      try {
        const data = JSON.parse(injectedScript.textContent);
        if (data.variants && Array.isArray(data.variants)) {
          variants = data.variants;
          console.log(`✅ Read ${variants.length} variants from injected qv-variant-data`);
          if (variants.length > 0 && variants[0].inventory_quantity === undefined) {
            console.warn('⚠️ inventory_quantity is MISSING! Pre‑order will not trigger.');
          }
        }
      } catch(e) { console.warn('Failed to parse injected variant data', e); }
    }
    
    if (!variants) {
      console.warn('No injected variant data found – pre-order disabled');
      return;
    }
    
    // ===== REPOSITION INVENTORY BADGE (above variant options) =====
    const inventoryBadge = container.querySelector('#variant-inventory');
    const variantSelector = container.querySelector('.product-option-selector-box, variant-selects, [data-variant-picker]');
    if (inventoryBadge && variantSelector && !inventoryBadge.hasAttribute('data-moved')) {
      inventoryBadge.setAttribute('data-moved', 'true');
      variantSelector.parentNode.insertBefore(inventoryBadge, variantSelector);
      console.log('✅ Inventory badge moved above variant options');
    }
    
    // ===== REPOSITION PRE-ORDER CONTAINER (below Add to Cart, above description) =====
    const consentContainer = container.querySelector('#dynamic-preorder-container');
    if (consentContainer && !consentContainer.hasAttribute('data-repositioned')) {
      consentContainer.setAttribute('data-repositioned', 'true');
      
      let addToCartBtn = container.querySelector('button.atc__button, button[data-add-to-cart], .checkout-btn');
      if (!addToCartBtn) {
        addToCartBtn = container.querySelector('.quickView-atc-button button');
      }
      
      let descriptionEl = container.querySelector('.product-detail-desc, .rte, .product__description');
      
      if (addToCartBtn && descriptionEl) {
        descriptionEl.parentNode.insertBefore(consentContainer, descriptionEl);
        console.log('✅ Pre-order box placed below Add to Cart button and above description');
      } else if (addToCartBtn) {
        addToCartBtn.parentNode.insertBefore(consentContainer, addToCartBtn.nextSibling);
        console.log('✅ Pre-order box placed after Add to Cart button (description not found)');
      } else {
        console.warn('Could not find Add to Cart button or description – pre-order box stays original position');
      }
    }
    
    // Quantity input (must exist)
    const qtyInput = container.querySelector('input[name="quantity"]');
    if (!qtyInput) {
      console.warn('Quickview: quantity input not found');
      return;
    }
    
    const consentCheckbox = container.querySelector('#preorder-consent-checkbox');
    const dynamicCheckoutBtn = container.querySelector('.shopify-payment-button');
    
    // ---- GET CURRENT VARIANT ID ----
    const getCurrentVariantId = () => {
      let variantId = null;
      const input = container.querySelector('input[name="id"]');
      if (input && input.value) variantId = input.value;
      if (!variantId) {
        const select = container.querySelector('select[name="id"]');
        if (select && select.value) variantId = select.value;
      }
      return variantId;
    };
    
    // Store badge element for updates
    const inventoryBadgeEl = container.querySelector('#variant-inventory');
    
    // ---- CHECK INVENTORY (badge update + pre‑order visibility) ----
    const checkInventory = () => {
      const variantId = getCurrentVariantId();
      if (!variantId) return;
      const variant = variants.find(v => v.id == variantId);
      if (!variant) {
        console.warn(`Variant ${variantId} not found`);
        return;
      }
      
      // Update the inventory badge (consistent HTML structure)
      if (inventoryBadgeEl) {
        const display = this.computeInventoryDisplay(variant);
        inventoryBadgeEl.innerHTML = `<p class="product-stock product-stock--${display.className}">${display.text}</p>`;
        // Keep the container class for styling hooks
        inventoryBadgeEl.classList.add('variant-inventory');
      }
      
      const stockRaw = variant.inventory_quantity;
      const qty = parseInt(qtyInput.value, 10) || 1;
      const available = variant.available === true;
      
      console.log(`Variant ${variantId}, stock = ${stockRaw}, qty = ${qty}, available = ${available}`);
      
      let isPreorder = false;
      if (!available) {
        isPreorder = false;
      } else if (stockRaw === null || stockRaw === undefined) {
        isPreorder = false;
        console.warn('⚠️ inventory_quantity missing → pre‑order disabled');
      } else {
        const stock = Number(stockRaw);
        if (stock === 0 && variant.inventory_policy === 'continue') {
          isPreorder = (qty > 0);
        } else if (stock > 0) {
          isPreorder = (qty > stock);
        }
      }
      
      if (isPreorder) {
        consentContainer.style.display = 'block';
        if (dynamicCheckoutBtn) dynamicCheckoutBtn.style.display = 'none';
      } else {
        consentContainer.style.display = 'none';
        if (consentCheckbox) consentCheckbox.checked = false;
        if (dynamicCheckoutBtn) dynamicCheckoutBtn.style.display = 'block';
      }
    };
    
    // ---- EVENT LISTENERS FOR INVENTORY RECHECKS ----
    qtyInput.addEventListener('change', checkInventory);
    qtyInput.addEventListener('input', checkInventory);
    
    const variantPicker = container.querySelector('select[name="id"], input[name="id"]');
    if (variantPicker) {
      variantPicker.addEventListener('change', () => setTimeout(checkInventory, 100));
    }
    
    container.addEventListener('click', (e) => {
      if (e.target.closest && (e.target.closest('.quantity__button') || e.target.closest('[data-quantity-update]'))) {
        setTimeout(checkInventory, 200);
      }
    });
    
    // ===== HANDLE ALL ADD‑TO‑CART BUTTONS (desktop + mobile) =====
    const addToCartSelector = [
      'button.atc__button',
      'button[data-add-to-cart]',
      '.atc__button',
      '.checkout-btn .atc__button',
      '.quickView-atc-button button',
      'button[name="add"]',            // standard Shopify Add to Cart
      '.product-form__cart-submit',    // common alternative
      'button[name="checkout"]'        // some themes use checkout as add
    ].join(',');
    
    // Disable / enable buttons every 300ms according to pre‑order state
    setInterval(() => {
      const buttons = container.querySelectorAll(addToCartSelector);
      const isPreorderMode = consentContainer.style.display === 'block';
      const isChecked = consentCheckbox && consentCheckbox.checked;
      
      buttons.forEach(btn => {
        if (isPreorderMode && !isChecked) {
          if (!btn.classList.contains('preorder-disabled-btn')) {
            btn.classList.add('preorder-disabled-btn');
            btn.disabled = true;
          }
        } else {
          if (btn.classList.contains('preorder-disabled-btn')) {
            btn.classList.remove('preorder-disabled-btn');
            btn.disabled = false;
          }
        }
      });
    }, 300);
    
    // Click interceptor – catches clicks on ANY matched button, even if added later
    container.addEventListener('click', (e) => {
      const btn = e.target.closest(addToCartSelector);
      if (btn && consentContainer.style.display === 'block' && consentCheckbox && !consentCheckbox.checked) {
        e.preventDefault();
        e.stopImmediatePropagation();
        alert('在庫数を超えるご注文が含まれています。\n「予約注文ポリシーに同意する」にチェックを入れてください。');
        return false;
      }
    }, true);
    
    // Run once to set initial state
    checkInventory();
  }
  
  // ---------- Core methods (updated) ----------
  setupEventDelegation() {
    console.log('🎯 Setting up event delegation...');
    document.addEventListener('click', (event) => { this.handleClick(event); });
    document.addEventListener('change', (event) => { this.handleChange(event); });
    document.addEventListener('cart:updated', () => this.updateCartInventoryBadges());
    document.addEventListener('cart:refresh', () => this.updateCartInventoryBadges());
    document.addEventListener('ajaxCart:render', () => this.updateCartInventoryBadges());
  }
  
  setupMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class' && mutation.target.classList.contains('variant_option')) {
          setTimeout(() => { this.checkAndUpdateVariant(); }, 200);
        }
        if (mutation.type === 'childList') {
          mutation.addedNodes?.forEach(node => {
            if (node.nodeType === 1 && node.matches && (node.matches('input.productOption') || node.querySelector('input.productOption'))) {
              console.log('New variant radio added, updating...');
              setTimeout(() => { this.checkAndUpdateVariant(); }, 300);
            }
          });
        }
      });
    });
    const variantContainer = document.querySelector('.product-option-selector-box, variant-selects');
    if (variantContainer) {
      observer.observe(variantContainer, { attributes: true, childList: true, subtree: true, attributeFilter: ['class', 'checked'] });
      console.log('👀 Mutation observer started');
    }
    const cartRoot = document.querySelector('.minicart__outerbox, [data-cart-form], form#cart');
    if (cartRoot) {
      const cartObserver = new MutationObserver(() => {
        clearTimeout(this._cartInvT);
        this._cartInvT = setTimeout(() => this.updateCartInventoryBadges(), 150);
      });
      cartObserver.observe(cartRoot, { childList: true, subtree: true });
      console.log('🛒 Cart inventory observer started');
    }
  }
  
  handleClick(event) {
    const variantOption = event.target.closest('.variant_option');
    if (variantOption) {
      console.log('🎯 Click on variant option');
      setTimeout(() => { this.checkAndUpdateVariant(); }, 100);
      return;
    }
    if (event.target.matches('input.productOption[type="radio"]')) {
      console.log('🎯 Click on radio button');
      setTimeout(() => { this.handleVariantSelection(event.target); }, 50);
    }
  }
  
  handleChange(event) {
    if (event.target.matches('input.productOption[type="radio"]')) {
      console.log('🔄 Radio button changed');
      setTimeout(() => { this.handleVariantSelection(event.target); }, 50);
    }
  }
  
  handleVariantSelection(radioElement) {
    const variantId = this.getVariantIdFromElement(radioElement);
    if (variantId) {
      console.log(`🔄 Selected variant ${variantId}: ${radioElement.value}`);
      this.updateInventory(variantId);
    }
  }
  
  checkAndUpdateVariant() {
    const variantId = this.getCurrentVariantId();
    if (!variantId) return;
    if (variantId !== this.currentVariantId) {
      this.currentVariantId = variantId;
      console.log(`🔄 Auto-detected variant change to ${variantId}`);
      this.updateInventory(variantId);
    }
  }
  
  getVariantIdFromElement(element) {
    if (!element) return null;
    if (element.getAttribute('data-variant-id')) {
      return parseInt(element.getAttribute('data-variant-id'));
    }
    const radio = element.querySelector('input.productOption[data-variant-id]');
    if (radio) {
      return parseInt(radio.getAttribute('data-variant-id'));
    }
    return null;
  }
  
  getCurrentVariantId() {
    const selectedRadio = document.querySelector('input.productOption[type="radio"]:checked');
    if (selectedRadio) {
      const variantId = selectedRadio.getAttribute('data-variant-id');
      if (variantId) return parseInt(variantId);
    }
    const selectedOption = document.querySelector('.variant_option.selected');
    if (selectedOption) {
      const radio = selectedOption.querySelector('input.productOption');
      if (radio) {
        const variantId = radio.getAttribute('data-variant-id');
        if (variantId) return parseInt(variantId);
      }
    }
    const jsonScript = document.querySelector('script[type="application/json"][data-selected-variant]');
    if (jsonScript) {
      try {
        const data = JSON.parse(jsonScript.textContent);
        if (data.id) return data.id;
      } catch(e) {}
    }
    return null;
  }
  
  updateOnLoad() {}
  
  updateInventory(variantId) {
    console.log(`updateInventory called for ${variantId}`);

    // Clear any pending update to debounce rapid changes
    clearTimeout(this._inventoryUpdateTimer);

    this._inventoryUpdateTimer = setTimeout(() => {
      // ---- 1. Get variant data from the page's JSON script ----
      const jsonScript = document.getElementById('ProductVariantsJson');
      if (!jsonScript) {
        console.warn('ProductVariantsJson not found – product page inventory badge cannot update');
        return;
      }

      let variants;
      try {
        const data = JSON.parse(jsonScript.textContent);
        variants = data.variants;
      } catch (e) {
        console.error('Error parsing ProductVariantsJson', e);
        return;
      }

      const variant = variants.find(v => v.id == variantId);
      if (!variant) {
        console.warn(`Variant ${variantId} not found in ProductVariantsJson`);
        return;
      }

      // ---- 2. Update the inventory badge (consistent HTML structure) ----
      const badge = document.getElementById('variant-inventory');
      if (!badge) {
        console.warn('#variant-inventory element not found');
        return;
      }

      const display = this.computeInventoryDisplay(variant);
      badge.innerHTML = `<p class="product-stock product-stock--${display.className}">${display.text}</p>`;
      // Keep the wrapper class for any additional styling
      badge.classList.add('variant-inventory');
    }, 50); // 50ms debounce merges rapid clicks
  }
  
  renderInventory(variant) {}
  
  computeInventoryDisplay(variant) {
    const qty = Number(variant.inventory_quantity ?? 0);
    const available = Boolean(variant.available);
    const policy = variant.inventory_policy || 'deny';
    let text = '';
    let className = '';

    if (available) {
      if (qty > 10) {
        text = '在庫あり';
        className = 'in';
      } else if (qty > 0) {
        if (policy === 'continue') {
          text = `残り ${qty} 点（追加分はご予約受付中）`;
          className = 'preorder-available';
        } else {
          text = `残り ${qty} 点です`;
          className = 'low';
        }
      } else {
        if (policy === 'continue') {
          text = '予約受付中';
          className = 'preorder';
        } else {
          text = '在庫切れ';
          className = 'out';
        }
      }
    } else {
      text = '在庫切れ';
      className = 'out';
    }
    return { text, className };
  }
  
  async updateCartInventoryBadges() {
    const els = document.querySelectorAll('[data-variant-inventory][data-variant-id]');
    if (!els.length) return;
    this._variantCache = this._variantCache || new Map();
    const fetchVariant = async (id) => {
      if (this._variantCache.has(id)) return this._variantCache.get(id);
      const p = fetch(`/variants/${id}.js`, { credentials: 'same-origin' })
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null);
      this._variantCache.set(id, p);
      return p;
    };
    await Promise.all(Array.from(els).map(async (el) => {
      const id = el.getAttribute('data-variant-id');
      if (!id) return;
      const variant = await fetchVariant(id);
      if (!variant) return;
      if (variant.inventory_quantity === undefined || variant.inventory_quantity === null) {
        if (variant.available === false) {
          el.textContent = '在庫切れ';
          el.classList.add('product-stock');
          el.classList.forEach((c) => { if (c.startsWith('product-stock--')) el.classList.remove(c); });
          el.classList.add('product-stock--out');
        }
        return;
      }
      const { text, className } = this.computeInventoryDisplay(variant);
      // For cart items we keep the original approach (textContent + class swap)
      // because they might be the <p> themselves. This ensures visual consistency.
      el.textContent = text;
      el.classList.add('product-stock');
      el.classList.forEach((c) => { if (c.startsWith('product-stock--')) el.classList.remove(c); });
      el.classList.add(`product-stock--${className}`);
    }));
  }
  
  generateInventoryHTML(variant) { return ''; }
  
  showError(message) {
    const container = document.getElementById('variant-inventory');
    if (container) {
      container.innerHTML = `<p class="product-stock product-stock--error">${message}</p>`;
    }
  }
  
  debug() {
    console.log('=== INVENTORY DEBUG ===');
    console.log('Product cache:', this.productCache);
  }
}

window.variantInventory = new VariantInventory();
window.debugInventory = () => { if (window.variantInventory) window.variantInventory.debug(); };

// ========== Pre-order Acknowledgements for Cart/Drawer (EXTENDED) ==========
(function () {
  function syncAckState() {
    if (window.__tfkAckEvaluate) window.__tfkAckEvaluate();
  }
  document.addEventListener('DOMContentLoaded', syncAckState);
  const mo = new MutationObserver(() => syncAckState());
  mo.observe(document.body, { childList: true, subtree: true });
})();

(function () {
  // Cache for variant inventory data used in cart checks
  const variantCache = new Map();

  async function fetchVariantWithCache(variantId) {
    if (variantCache.has(variantId)) return variantCache.get(variantId);
    try {
      const res = await fetch(`/variants/${variantId}.js`, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const variant = await res.json();
      variantCache.set(variantId, variant);
      return variant;
    } catch(e) {
      console.warn(`Failed to fetch variant ${variantId}`, e);
      return null;
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart.js');
    return res.json();
  }

  function calcPreorderCount(cart) {
    let count = 0;
    (cart.items || []).forEach((item) => {
      const v = item.variant || item;
      const available = v.available === true || item.available === true;
      const inventoryPolicy = (v.inventory_policy || item.inventory_policy || '').toString();
      const hasQty = typeof v.inventory_quantity === 'number' || typeof item.inventory_quantity === 'number';
      const qty = (typeof v.inventory_quantity === 'number' ? v.inventory_quantity : typeof item.inventory_quantity === 'number' ? item.inventory_quantity : null);
      const isPreorder = available && inventoryPolicy === 'continue' && hasQty && qty <= 0;
      if (isPreorder) count += (item.quantity || 0);
    });
    return count;
  }

  function setDrawerAckUI(preorderCount, exceededMessage = null) {
    document.querySelectorAll('[data-preorder-ack-container]').forEach((box) => {
      const checkbox = box.querySelector('[data-preorder-ack-checkbox]');
      const textSpan = box.querySelector('[data-preorder-ack-text]');
      const shouldShow = (preorderCount > 0) || (exceededMessage !== null);
      
      if (!shouldShow) {
        box.style.display = 'none';
        box.dataset.hasPreorder = '0';
        if (checkbox) checkbox.checked = false;
        if (window.__tfkAckEvaluate) window.__tfkAckEvaluate();
        return;
      }
      
      box.style.display = '';
      box.dataset.hasPreorder = '1';
      if (window.__tfkAckEvaluate) window.__tfkAckEvaluate();
      
      let message = '';
      if (exceededMessage) {
        message = exceededMessage;
      } else if (preorderCount > 0) {
        message = `このカートには${preorderCount}点の予約商品が含まれております。<br>予約商品のお届けには、約2週間のお時間を頂戴する場合がございます。<br>なお、ご注文後の予約商品は、お客様のご都合によるキャンセルをお受けいたしかねます。`;
      }
      if (textSpan) textSpan.innerHTML = message;
      
      const applyDisabled = () => {
        const ok = checkbox && checkbox.checked;
        document.querySelectorAll('button[name="checkout"], #cartCheckout, [data-cart-checkout-buttons] button, .ajax-cart__checkout button').forEach((btn) => (btn.disabled = !ok));
      };
      if (checkbox && !checkbox.dataset.bound) {
        checkbox.dataset.bound = '1';
        checkbox.addEventListener('change', applyDisabled);
      }
      applyDisabled();
    });
  }

  function calcPreorderCountFromDOM() {
    let total = 0;
    const badges = document.querySelectorAll('.product-stock--preorder');
    badges.forEach((badge) => {
      const row = badge.closest('.cart-item, .ajax-cart__product, .minicart__item, li, .cart__item') || badge.parentElement;
      let qty = 1;
      if (row) {
        const qtyInput = row.querySelector('input[name="updates[]"]') || row.querySelector('input.quantity-input') || row.querySelector('input[type="number"]');
        if (qtyInput) {
          const v = parseInt(qtyInput.value, 10);
          if (!Number.isNaN(v) && v > 0) qty = v;
        }
      }
      total += qty;
    });
    return total;
  }

  // Check if any cart item quantity exceeds its actual stock
  async function checkQuantityExceedsStock() {
    const cartItems = document.querySelectorAll('.cart__item[data-cart-item], .cart-item, .ajax-cart__product, .minicart__item');
    for (const item of cartItems) {
      const variantIdElem = item.querySelector('[data-variant-id]');
      const variantId = variantIdElem ? variantIdElem.getAttribute('data-variant-id') : null;
      if (!variantId) continue;
      
      const qtyInput = item.querySelector('input[name="updates[]"], input.quantity-input, input[type="number"]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      
      const variant = await fetchVariantWithCache(variantId);
      if (!variant) continue;
      
      const stockRaw = variant.inventory_quantity;
      if (typeof stockRaw === 'number' && !isNaN(stockRaw) && stockRaw > 0 && qty > stockRaw) {
        return `一部の商品の数量が在庫数を超えています。<br>予約商品のお届けには、約2週間のお時間を頂戴する場合がございます。<br>なお、ご注文後の予約商品は、お客様のご都合によるキャンセルをお受けいたしかねます。`;
      }
    }
    return null;
  }

  async function refreshPreorderAck() {
    try {
      const domCount = calcPreorderCountFromDOM();
      let exceededMessage = null;
      if (domCount === 0) {
        // Only check quantity exceed if there are no pre-order items already
        exceededMessage = await checkQuantityExceedsStock();
      }
      if (domCount > 0 || exceededMessage) {
        setDrawerAckUI(domCount, exceededMessage);
        return;
      }
      const cart = await fetchCart();
      const count = calcPreorderCount(cart);
      if (count > 0) {
        setDrawerAckUI(count, null);
      } else {
        setDrawerAckUI(0, null);
      }
    } catch (e) {
      console.warn(e);
    }
  }

  document.addEventListener('DOMContentLoaded', refreshPreorderAck);
  const mo = new MutationObserver(() => {
    clearTimeout(window.__preorderAckTO);
    window.__preorderAckTO = setTimeout(refreshPreorderAck, 80);
  });
  mo.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('click', (e) => {
    const inDrawer = e.target.closest('.cart-drawer, .minicart__outerbox, [data-ajax-cart]');
    if (!inDrawer) return;
    setTimeout(refreshPreorderAck, 250);
    setTimeout(refreshPreorderAck, 800);
  });
})();

(function () {
  function isVisible(el) {
    if (!el) return false;
    if (el.style && el.style.display === 'none') return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }
  function getCheckoutButtons(scope) {
    return Array.from((scope || document).querySelectorAll('button[name="checkout"], #cartCheckout, [data-cart-checkout-buttons] button, .ajax-cart__checkout button'));
  }
  function evaluate(scope) {
    const root = scope || document;
    const terms = root.querySelector('[data-terms-ack-checkbox]');
    const termsOk = !terms || terms.checked;
    const preorderBoxes = Array.from(root.querySelectorAll('[data-preorder-ack-container]'));
    let preorderOk = true;
    preorderBoxes.forEach((box) => {
      if (!box) return;
      const required = isVisible(box);
      if (!required) return;
      const cb = box.querySelector('[data-preorder-ack-checkbox]');
      if (!cb || !cb.checked) preorderOk = false;
    });
    const ok = termsOk && preorderOk;
    getCheckoutButtons(root).forEach((btn) => {
      btn.disabled = !ok;
      btn.classList.toggle('is-disabled-by-ack', !ok);
    });
  }
  function bindOnce() {
    document.querySelectorAll('[data-terms-ack-checkbox], [data-preorder-ack-checkbox]').forEach((cb) => {
      if (cb.dataset.ackBound) return;
      cb.dataset.ackBound = '1';
      cb.addEventListener('change', () => evaluate());
    });
    window.__tfkAckEvaluate = evaluate;
    evaluate();
  }
  document.addEventListener('DOMContentLoaded', bindOnce);
  const mo = new MutationObserver(() => { bindOnce(); });
  mo.observe(document.body, { childList: true, subtree: true });
})();